/**
 * Homepage component for Manatee Labs website.
 * Follows the design specifications from manatee-labs-full-spec.md
 */
import { useState, useEffect } from "react";
import JsonData from "../data/data.json";
import { motion } from "framer-motion";
import "./home.css";
import { Brain, Users, Cloud } from "lucide-react";

// Define types for our data structure
interface HeaderData {
  headline: string;
  subheadline: string;
  ctaText: string;
}

interface WhatWeDoData {
  title: string;
  description: string;
}

interface FeatureItem {
  icon: string;
  title: string;
  description: string;
}

interface WhyChooseUsData {
  title: string;
  features: FeatureItem[];
}

interface HomePageData {
  Header?: HeaderData;
  WhatWeDo?: WhatWeDoData;
  WhyChooseUs?: WhyChooseUsData;
}

export const Home = () => {
  const [homePageData, setHomePageData] = useState<HomePageData>({});

  useEffect(() => {
    setHomePageData(JsonData);
  }, []);

  // Animation variants for fade-in effect
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <div>
      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="overlay">
          <div className="container">
            <motion.div
              className="hero-content"
              initial="hidden"
              animate="visible"
              variants={fadeIn}
            >
              <h1>
                {homePageData.Header?.headline ||
                  "AI-First Amazon Connect Solutions"}
              </h1>
              <p className="subheadline">
                {homePageData.Header?.subheadline || "Loading..."}
              </p>
              <motion.a
                href="#contact"
                className="cta-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {homePageData.Header?.ctaText || "Get Started Today"}
              </motion.a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section id="what-we-do" className="section">
        <div className="container">
          <motion.div
            className="section-content"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="section-title">
              {homePageData.WhatWeDo?.title || "What We Do"}
            </h2>
            <div
              className="section-description"
              dangerouslySetInnerHTML={{
                __html:
                  homePageData.WhatWeDo?.description?.replace(
                    /\*\*(.*?)\*\*/g,
                    "<strong>$1</strong>"
                  ) || "",
              }}
            />
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-choose-us" className="section accent-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="section-title">
              {homePageData.WhyChooseUs?.title || "Why Choose Manatee Labs?"}
            </h2>
          </motion.div>

          <div className="features-grid">
            {homePageData.WhyChooseUs?.features?.map((feature, index) => (
              <motion.div
                className="feature-card"
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: 0.6,
                      delay: index * 0.2,
                    },
                  },
                }}
                whileHover={{
                  y: -10,
                  boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                }}
              >
                <div className="feature-icon">
                  {feature.icon === "brain" && (
                    <Brain size={48} color="#173FA3" />
                  )}
                  {feature.icon === "users" && (
                    <Users size={48} color="#173FA3" />
                  )}
                  {feature.icon === "cloud" && (
                    <Cloud size={48} color="#173FA3" />
                  )}
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
